import { type Config, config } from "@repo/config";
import type { Purchase } from "@repo/database";

const plans = config.payments.plans as Config["payments"]["plans"];

type PlanId = keyof typeof config.payments.plans;
type PurchaseWithoutTimestamps = Omit<Purchase, "createdAt" | "updatedAt">;

function getActivePlanFromPurchases(purchases?: PurchaseWithoutTimestamps[]) {
	// 首先查找状态为active的订阅
	const activeSubscription = purchases?.find(
		(purchase) =>
			purchase.type === "SUBSCRIPTION" && purchase.status === "active",
	);

	// 如果找到active订阅，返回它
	if (activeSubscription) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === activeSubscription.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === activeSubscription.productId,
			),
			status: activeSubscription.status,
			purchaseId: activeSubscription.id,
			interval: activeSubscription.interval || null,
		};
	}

	// 如果没有active订阅，查找cancelled订阅，并按创建时间排序，返回最近的一个
	const cancelledSubscriptions = purchases
		?.filter(
			(purchase) =>
				purchase.type === "SUBSCRIPTION" &&
				purchase.status === "cancelled",
		)
		.sort((a, b) => {
			// 由于我们移除了时间戳，这里我们可以使用ID比较
			// 一般情况下，ID越新创建时间越晚
			return a.id.localeCompare(b.id) * -1; // -1 使排序为降序（最新的在前）
		});

	// 如果找到cancelled订阅，返回最近的一个
	if (cancelledSubscriptions && cancelledSubscriptions.length > 0) {
		const latestCancelledSubscription = cancelledSubscriptions[0];
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) =>
					price.productId === latestCancelledSubscription.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) =>
					price.productId === latestCancelledSubscription.productId,
			),
			status: latestCancelledSubscription.status,
			purchaseId: latestCancelledSubscription.id,
			interval: latestCancelledSubscription.interval || null,
		};
	}

	// 查找一次性购买
	const oneTimePurchase = purchases?.find(
		(purchase) => purchase.type === "ONE_TIME",
	);

	if (oneTimePurchase) {
		const plan = Object.entries(plans).find(([_, plan]) =>
			plan.prices?.some(
				(price) => price.productId === oneTimePurchase.productId,
			),
		);

		return {
			id: plan?.[0] as PlanId,
			price: plan?.[1].prices?.find(
				(price) => price.productId === oneTimePurchase.productId,
			),
			status: "active",
			purchaseId: oneTimePurchase.id,
			interval: oneTimePurchase.interval || null,
		};
	}

	// 最后才返回免费计划
	const freePlan = Object.entries(plans).find(([_, plan]) => plan.isFree);

	return freePlan
		? {
				id: freePlan[0] as PlanId,
				status: "active",
				interval: null, // 免费计划没有订阅周期
			}
		: null;
}

export function createPurchasesHelper(purchases: PurchaseWithoutTimestamps[]) {
	const activePlan = getActivePlanFromPurchases(purchases);

	const hasSubscription = (planIds?: PlanId[] | PlanId) => {
		return (
			!!activePlan &&
			(Array.isArray(planIds)
				? planIds.includes(activePlan.id)
				: planIds === activePlan.id)
		);
	};

	const hasPurchase = (planId: PlanId) => {
		return !!purchases?.some((purchase) =>
			Object.entries(plans)
				.find(([id]) => id === planId)?.[1]
				.prices?.some(
					(price) => price.productId === purchase.productId,
				),
		);
	};

	return { activePlan, hasSubscription, hasPurchase };
}
